import { Controller } from "@hotwired/stimulus"

// Collapses the left rail to an icon strip and remembers the choice.
//
// Close cousin of `disclosure`, but deliberately not the same controller: that one hides a
// content target and remembers nothing at all. This state has to be readable by the SERVER — it
// renders the collapsed class itself so a reload paints the rail at the right width instead of
// opening it and snapping it shut — and a cookie is the only store both ends can see. It is
// unsigned for the same reason: this writes it without a round trip, and JS cannot produce a
// signature Rails would accept. `project-disclosure` is the sibling that does the same for the
// project rows inside the rail.
//
// A year, because a preference that quietly resets is worse than none.
const COOKIE_MAX_AGE_SECONDS = 31536000

export default class extends Controller {
  static targets = ["toggle"]
  static values = { cookie: String }

  toggle() {
    this.#apply(!this.#collapsed)
  }

  // "+ Project" opens fields that cannot fit a 56px strip, so it expands the rail on the way in.
  // Guarded rather than unconditional so an ordinary click in an already-open rail does not
  // rewrite the cookie.
  expand() {
    if (this.#collapsed) this.#apply(false)
  }

  #apply(collapsed) {
    this.element.classList.toggle("is-sidebar-collapsed", collapsed)

    // The label is fixed; only the pressed state moves. See the comment on the button.
    if (this.hasToggleTarget) this.toggleTarget.setAttribute("aria-pressed", collapsed ? "true" : "false")

    this.#persist(collapsed)
  }

  get #collapsed() {
    return this.element.classList.contains("is-sidebar-collapsed")
  }

  #persist(collapsed) {
    const secure = location.protocol === "https:" ? "; Secure" : ""

    document.cookie =
      `${this.cookieValue}=${collapsed ? "1" : "0"}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`
  }
}
